import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessorConnector } from '../control-value-accessor-connector';
import { NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import {
  SelectInputType,
  SelectOption,
} from '../../../../core/models/select-option';
import { compareObjects, splitPascalCase } from '../../../../core/utils';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  map,
  Observable,
  ReplaySubject,
  Subject,
  switchMap,
  take,
  takeUntil,
  throwError,
} from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectInputRetrieveOptions } from '../../../../core/models/select-input-retrieve-options';
import { ApiSearchResponse } from '../../../../core/models/api-search-response';
import { TranslateService } from '@ngx-translate/core';
import { ConnectionPositionPair } from '@angular/cdk/overlay';

@UntilDestroy()
@Component({
  selector: 'ling-select-input',
  templateUrl: './select-input.component.html',
  styleUrls: ['./select-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: SelectInputComponent,
      multi: true,
    },
  ],
})
export class SelectInputComponent
  extends ControlValueAccessorConnector
  implements OnInit, OnChanges
{
  @ViewChild('dropdown') dropdownRef!: ElementRef;
  @ViewChild('inputContainer') inputContainerRef!: ElementRef;
  @ViewChild('searchInput') searchInputRef!: ElementRef;
  @Output() actionButtonPress = new EventEmitter();
  @Output() valueChange = new EventEmitter<any>();
  @Output() removeButtonPress = new EventEmitter();
  @Output() actionOption = new EventEmitter();
  @Output() remoteOptionsLoaded = new EventEmitter();
  @Output() dropdownToggle = new EventEmitter<boolean>();

  @Input() type: SelectInputType = SelectInputType.Other;
  @Input() removeButton = false;
  @Input() mode: 'select' | 'actions' = 'select';
  @Input() info = '';
  @Input() actionButton = '';
  @Input() placeholder = 'Select';
  @Input() multiple = false;
  @Input() label = '';
  @Input() isSearchable = false;
  @Input() disabledInput = false;
  @Input() noOptionsText = 'No options';
  @Input() retrieveOptionsSettings?: SelectInputRetrieveOptions;

  @Input() set options(newOptions: (SelectOption<any> | string | number)[]) {
    const selectedValues = Array.isArray(this.control.value)
      ? this.control.value
      : [this.control.value];
    this._options = newOptions.map((option) => {
      return typeof option === 'object'
        ? new SelectOption<unknown>(
            option.text,
            option.value,
            undefined,
            option.disabled
          )
        : new SelectOption<unknown>(String(option), option);
    });
    this.displayedOptions = this._options;
    if (this._options.length) {
      const valuesToSelect = selectedValues.length
        ? selectedValues
        : this.pendingValues;
      this.selectValues(valuesToSelect);
      this.pendingValues = [];
    }
  }

  private _dropdownOpened = false;
  _options: SelectOption<unknown>[] = [];
  loadedRemoteOptions$ = new ReplaySubject<SelectOption<any>[]>(1);
  searchOptions: SelectOption<unknown>[] = [];
  displayedOptions = this._options;
  highlightedOptionIndex: number | null = null;
  isFocused = false;
  searchDebounceTimeMs = 100;
  readonly actionButtonIndex = -1;
  updateDropdownPosition$ = new Subject<void>();
  dropdownPositionUpdateThrottleMS = 20;
  pendingValues: unknown[] = [];
  dropdownDirection: 'up' | 'down' = 'down';
  pageSize = 50;
  optionsQuery: {
    [key: string]: any;
    $limit: number;
    $skip: number;
    name?: { $regex: string; $options: 'i' };
    id?: string;
  } = {
    $limit: this.pageSize,
    $skip: 0,
  };
  textSearchQuery$ = new BehaviorSubject<string>('');
  optionsLoading = false;
  stopExistingResponseListeners = new Subject<void>();
  optionsLoadingState = new EventEmitter<boolean>();
  selectInputType = SelectInputType;

  @HostBinding('tabIndex') get tabIndex() {
    return this.disabledInput ? -1 : 0;
  }

  @HostListener('window:click', ['$event.target'])
  onOutsideClick(target: EventTarget) {
    if (!this.elementRef.nativeElement.contains(target) && this.isFocused) {
      this.onFocusOut();
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (this._dropdownOpened) {
      this.updateDropdownPosition$.next();
    }
  }

  @HostListener('window:scroll', ['$event'])
  onScroll() {
    if (this._dropdownOpened) {
      this.toggleDropdown(false);
    }
  }

  @HostListener('focusin', ['$event'])
  focusinHandler(event: FocusEvent) {
    if (event.target !== this.removeInputButtonElement && !this.disabledInput) {
      if (this.isFocused) {
        return;
      }
      if (
        this.isSearchable &&
        event.target !== this.searchInputRef.nativeElement
      ) {
        this.searchInputRef.nativeElement.focus();
      }
      this.isFocused = true;
      this.cd.markForCheck();
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (this.isFocused && !this._dropdownOpened && event.key === 'Enter') {
      this.toggleDropdown(true);
    }
    if (this.isFocused && event.key === 'Tab') {
      this.onTabPressed(event);
    }
    if (this._dropdownOpened && this.displayedOptions.length) {
      const pressedKey = event.key;
      event.stopPropagation();
      switch (pressedKey) {
        case 'ArrowUp': {
          this.onArrowUpPressed(event);
          break;
        }
        case 'ArrowDown': {
          this.onArrowDownPressed(event);
          break;
        }
        case 'Enter': {
          event.preventDefault();
          this.onEnterPressed();
          break;
        }
        case 'Escape': {
          this.onEscapePressed();
          break;
        }
      }
    }
  }

  static getOptionsFromEnum<T>(sourceEnum: {
    [key: string]: string;
  }): SelectOption<T>[] {
    return Object.entries(sourceEnum).map(
      (entry) =>
        new SelectOption<T>(
          splitPascalCase(entry[0]) as string,
          entry[1] as unknown as T
        )
    );
  }

  dropdownPosition$ = new Subject<ConnectionPositionPair>();
  dropdownPositions: ConnectionPositionPair[] = [
    {
      originX: 'center',
      originY: 'bottom',
      offsetY: 5,
      offsetX: 0,
      overlayX: 'center',
      overlayY: 'top',
    },
    {
      originX: 'center',
      originY: 'top',
      offsetY: -5,
      offsetX: 0,
      overlayX: 'center',
      overlayY: 'bottom',
    },
  ];

  constructor(
    injector: Injector,
    private elementRef: ElementRef,
    private sanitizer: DomSanitizer,
    private cd: ChangeDetectorRef,
    private translateService: TranslateService
  ) {
    super(injector, translateService);
  }

  override ngOnInit() {
    super.ngOnInit();
    this.initInputErrorListener();
    this.initTextSearchListener();
  }

  override ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
    if (changes['retrieveOptionsSettings'] && this.retrieveOptionsSettings) {
      this.retrieveOptions();
    }
  }

  initInputErrorListener(): void {
    this.error$.subscribe(() => {
      this.updateDropdownPosition$.next();
    });
  }

  initTextSearchListener(): void {
    this.textSearchQuery$
      .pipe(debounceTime(this.searchDebounceTimeMs))
      .subscribe((query) => {
        this.onTextSearch(query);
      });
  }

  onTextSearch(query: string): void {
    if (query) {
      this.searchOptions = this._options.filter((option) =>
        option.text.toLowerCase().includes(query.toLowerCase())
      );
      this.displayedOptions = this.searchOptions;
      this.toggleDropdown(true);
    } else {
      this.displayedOptions = this._options;
      this.cd.markForCheck();
    }
  }

  retrieveOptions(): void {
    if (!this.retrieveOptionsSettings) {
      return;
    }
    this.stopExistingResponseListeners.next();
    this.optionsLoading = true;
    this.optionsLoadingState.emit(true);

    this.retrieveOptionsSettings
      .call({
        ...this.retrieveOptionsSettings.constantQuery,
        ...this.optionsQuery,
        $limit: 0,
      })
      .pipe(switchMap((res) => this.getAllOptionsCall(res.total!)))
      .subscribe({
        next: (res: ApiSearchResponse<SelectOption<any>>[]) => {
          this.optionsLoading = false;
          this.optionsLoadingState.emit(false);
          this.options = res.map((resChunk: any) => resChunk.data).flat();
          this.remoteOptionsLoaded.emit(this._options);
          this.loadedRemoteOptions$.next(this._options);
          this.cd.detectChanges();
        },
        error: () => {
          this.optionsLoading = false;
          this.optionsLoadingState.emit(false);
          this.cd.detectChanges();
        },
      });
  }

  getOptionsCall(
    query: typeof this.optionsQuery
  ): Observable<ApiSearchResponse<SelectOption<any>>> | undefined {
    return this.retrieveOptionsSettings
      ?.call(query, this.retrieveOptionsSettings?.feathersMatcher)
      .pipe(
        untilDestroyed(this),
        map(this.retrieveOptionsSettings.mapper),
        takeUntil(this.stopExistingResponseListeners)
      );
  }

  getAllOptionsCall(
    total: number
  ): Observable<ApiSearchResponse<SelectOption<any>>[]> {
    if (!total) {
      return throwError(() => 'No editions to watch');
    }
    const calls: Observable<ApiSearchResponse<SelectOption<any>>>[] = [];
    while (calls.length < Math.ceil(total / this.pageSize)) {
      calls.push(
        this.getOptionsCall({
          ...this.retrieveOptionsSettings!.constantQuery,
          ...this.optionsQuery,
          $limit: this.pageSize,
          $skip: this.pageSize * calls.length,
        })!
      );
    }
    return combineLatest(calls).pipe(
      take(1),
      untilDestroyed(this),
      takeUntil(this.stopExistingResponseListeners)
    );
  }

  override writeValue(value: unknown[] | unknown) {
    if (!value) {
      return;
    }
    let newValues = Array.isArray(value) ? value : [value];
    super.writeValue(newValues);
    if (!this._options.length) {
      this.pendingValues = newValues;
    }
    setTimeout(() => {
      this.selectValues(newValues);
    });
  }

  get removeInputButtonElement(): HTMLButtonElement | null {
    return (this.elementRef.nativeElement as HTMLElement).querySelector(
      '#removeInputButton'
    );
  }

  get dropdownOpened(): boolean {
    return this._dropdownOpened;
  }

  get dropdownStyle(): CSSStyleDeclaration | undefined {
    return this.dropdownRef?.nativeElement.style;
  }

  get isRequired(): boolean {
    return this.control.hasValidator(Validators.required);
  }

  get firstSelectedOption(): SelectOption<any> | undefined {
    return this._options.find((option) => option.selected);
  }

  get searchInputText(): string {
    let text = this.firstSelectedOption?.text;
    if (this.multiple) {
      text = this.isSearchable ? '' : this.placeholder;
    } else {
      if (this.isSearchable && !this.isFocused) {
        text = this.firstSelectedOption?.text;
      }
    }
    if (this.mode === 'actions') {
      text = this.placeholder;
    }
    return text ?? '';
  }

  scrollHighlightedOptionIntoView(): void {
    setTimeout(() => {
      this.elementRef.nativeElement
        .querySelector('.highlighted-option')
        ?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
    });
  }

  onArrowUpPressed(event: KeyboardEvent): void {
    event.preventDefault();
    const activeOptions = this.displayedOptions
      .map((option, index) => ({ globalIndex: index, option }))
      .filter((option) => !option.option.disabled);

    if (!activeOptions.length && !this.actionButton) {
      return;
    }

    const lastActiveOption = activeOptions.at(-1)!;
    const selectedActiveOptionIndex = activeOptions.findIndex(
      (option) => option.globalIndex === this.highlightedOptionIndex
    );

    if (this.highlightedOptionIndex === null) {
      if (this.actionButton && this.dropdownDirection === 'down') {
        this.highlightedOptionIndex = this.actionButtonIndex;
      } else {
        this.highlightedOptionIndex = activeOptions.length
          ? lastActiveOption.globalIndex
          : null;
      }
      this.scrollHighlightedOptionIntoView();
      return;
    }

    if (this.highlightedOptionIndex === this.actionButtonIndex) {
      this.highlightedOptionIndex = activeOptions.length
        ? lastActiveOption.globalIndex
        : null;
      this.scrollHighlightedOptionIntoView();
      return;
    }

    if (selectedActiveOptionIndex > 0) {
      this.highlightedOptionIndex =
        activeOptions[selectedActiveOptionIndex - 1].globalIndex;
      this.scrollHighlightedOptionIntoView();
      return;
    }

    if (selectedActiveOptionIndex === 0) {
      this.highlightedOptionIndex = this.actionButton
        ? this.actionButtonIndex
        : lastActiveOption.globalIndex;
      this.scrollHighlightedOptionIntoView();
      return;
    }
  }

  onArrowDownPressed(event: KeyboardEvent): void {
    event.preventDefault();

    const activeOptions = this.displayedOptions
      .map((option, index) => ({ globalIndex: index, option }))
      .filter((option) => !option.option.disabled);

    if (!activeOptions.length && !this.actionButton) {
      return;
    }

    const lastActiveOption = activeOptions.at(-1)!;
    const firstActiveOption = activeOptions.at(0)!;
    const selectedActiveOptionIndex = activeOptions.findIndex(
      (option) => option.globalIndex === this.highlightedOptionIndex
    );

    if (this.highlightedOptionIndex === null) {
      if (this.dropdownDirection === 'up' && this.actionButton) {
        this.highlightedOptionIndex = this.actionButtonIndex;
        return;
      }
      this.highlightedOptionIndex = firstActiveOption.globalIndex;
      this.scrollHighlightedOptionIntoView();
      return;
    }

    if (this.highlightedOptionIndex < lastActiveOption.globalIndex) {
      this.highlightedOptionIndex =
        activeOptions[selectedActiveOptionIndex + 1].globalIndex;
      this.scrollHighlightedOptionIntoView();
      return;
    }

    if (this.highlightedOptionIndex === lastActiveOption.globalIndex) {
      if (this.actionButton) {
        this.highlightedOptionIndex = this.actionButtonIndex;
        return;
      }
      this.highlightedOptionIndex = firstActiveOption.globalIndex;
      this.scrollHighlightedOptionIntoView();
      return;
    }
  }

  onEscapePressed(): void {
    if (this._dropdownOpened) {
      this.toggleDropdown(false);
    }
  }

  onEnterPressed(): void {
    const activeOptions = this.displayedOptions
      .map((option, index) => ({ globalIndex: index, option }))
      .filter((option) => !option.option.disabled);

    if (!activeOptions.length && !this.actionButton) {
      return;
    }
    const selectedActiveOptionIndex = activeOptions.findIndex(
      (option) => option.globalIndex === this.highlightedOptionIndex
    );

    if (this.highlightedOptionIndex === this.actionButtonIndex) {
      this.onActionButtonClick();
      return;
    }

    if (
      this.highlightedOptionIndex ||
      (this.highlightedOptionIndex === 0 && selectedActiveOptionIndex !== -1)
    ) {
      this.toggleOption(this.displayedOptions[this.highlightedOptionIndex]);
    }
  }

  onTabPressed(event: any): void {
    event.stopPropagation();
    event.target.blur();
    this.onFocusOut();
  }

  toggleOption(changedOption: SelectOption<any>) {
    if (this.mode === 'select') {
      changedOption.selected = !changedOption.selected;
      if (changedOption.selected && !this.multiple) {
        this._options.forEach((option) =>
          option !== changedOption ? (option.selected = false) : ''
        );
      }

      let newControlValue;

      if (this.multiple) {
        newControlValue = this._options
          .filter((option) => option.selected)
          .map((option) => option.value);
      } else {
        newControlValue = this.firstSelectedOption?.value;
        this.toggleDropdown(false);
      }

      if (changedOption.selected && !this.multiple) {
        this.textSearchQuery$.next('');
        this.searchInputRef.nativeElement.value = '';
      }

      this.valueChange.emit(
        newControlValue
          ? JSON.parse(JSON.stringify(newControlValue))
          : newControlValue
      );
      this.onChange(
        newControlValue
          ? JSON.parse(JSON.stringify(newControlValue))
          : newControlValue
      );
    } else {
      this.actionOption.emit(changedOption);
      this.toggleDropdown(false);
    }
  }

  selectValues(selectedValues: unknown[]): void {
    this._options.forEach((option) => {
      const foundSelectedValue = selectedValues.find((selectedOption) => {
        if (typeof option.value === 'object') {
          return compareObjects<object>(
            option.value!,
            selectedOption as object
          );
        } else {
          return option.value === selectedOption;
        }
      });
      option.selected = !!foundSelectedValue;
    });
    this.cd.detectChanges();
  }

  toggleDropdown(state?: boolean): void {
    this._dropdownOpened = state ?? !this._dropdownOpened;
    if (this.disabledInput || this.optionsLoading) {
      this._dropdownOpened = false;
    }

    this.updateDropdownPosition$.next();

    if (!this._dropdownOpened) {
      this.onTouched();
      this.highlightedOptionIndex = null;
      this.displayedOptions = this._options;

      if (this.searchInputRef.nativeElement.value) {
        this.searchInputRef.nativeElement.value = this.searchInputText;
      }
    }

    this.cd.markForCheck();
    this.dropdownToggle.emit(this._dropdownOpened);
  }

  onFocusOut(): void {
    this.toggleDropdown(false);
    this.textSearchQuery$.next('');
    this.isFocused = false;
    this.searchInputRef.nativeElement.value = this.searchInputText;
  }

  onSearchInput(event: any): void {
    this.textSearchQuery$.next(event.target.value);
  }

  getOptionTextHTML(text: string): SafeHtml {
    let textHtml = text;
    if (this.textSearchQuery$.value) {
      const query = this.textSearchQuery$.value;
      const indexOfQueryString = text
        .toLowerCase()
        .indexOf(query.toLowerCase());

      if (indexOfQueryString > -1) {
        const prefix = text.slice(0, indexOfQueryString);
        const bold = text.slice(
          indexOfQueryString,
          indexOfQueryString + query.length
        );
        const postfix = text.slice(
          indexOfQueryString + query.length,
          text.length
        );
        textHtml = `${prefix}<b>${bold}</b>${postfix}`;
      } else {
        textHtml = text;
      }
    }
    return this.sanitizer.bypassSecurityTrustHtml(textHtml);
  }

  onInputContainerClick(): void {
    if (!this.isSearchable && !this.disabledInput) {
      this.toggleDropdown();
    }
  }

  onDropdownToggleButtonClick(): void {
    if (this.isSearchable && !this.disabledInput) {
      this.toggleDropdown();
    }
  }

  onActionButtonClick(): void {
    this.actionButtonPress.emit();
    this.onFocusOut();
  }
}
